/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { Component, DestroyRef, OnInit, ViewEncapsulation, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslatePipe } from '@ngx-translate/core';
import { MigrationApiService } from './migration-api.service';
import { MigrationOverviewComponent } from './migration-overview.component';
import { MigrationWizardComponent } from './migration-wizard.component';
import { MigrationConnectionsComponent } from './migration-connections.component';
import { MigrationJobsComponent } from './migration-jobs.component';
import { MigrationMappingComponent } from './migration-mapping.component';
import { MigrationValidationComponent } from './migration-validation.component';
import { MigrationHistoryComponent } from './migration-history.component';
import { MigrationErrorsComponent } from './migration-errors.component';
import { MigrationReconciliationComponent } from './migration-reconciliation.component';
import { MigrationSettingsComponent } from './migration-settings.component';

export type MigrationTab =
  | 'overview'
  | 'new'
  | 'connections'
  | 'jobs'
  | 'mapping'
  | 'validation'
  | 'history'
  | 'errors'
  | 'reconciliation'
  | 'settings';

/**
 * The Data Migration center — one screen inside Settings with its own
 * sub-navigation. The active tab lives in the URL (`settings/data-migration/
 * <tab>`) so a section can be deep-linked and browser-back works.
 *
 * Everything execution-shaped — test connection, discovery, dry run, run,
 * retry, reconcile — goes through MigrationApiService, whose `available`
 * signal says whether the sidecar actually implements it. Where it does not,
 * the screens say so instead of pretending.
 */
@Component({
  selector: 'app-migration-center',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    TranslatePipe,
    MigrationOverviewComponent,
    MigrationWizardComponent,
    MigrationConnectionsComponent,
    MigrationJobsComponent,
    MigrationMappingComponent,
    MigrationValidationComponent,
    MigrationHistoryComponent,
    MigrationErrorsComponent,
    MigrationReconciliationComponent,
    MigrationSettingsComponent
  ],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './migration-center.component.html',
  styleUrl: './migration-center.component.scss'
})
export class MigrationCenterComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  readonly api = inject(MigrationApiService);

  readonly tab = signal<MigrationTab>('overview');

  readonly tabs: { id: MigrationTab; labelKey: string }[] = [
    { id: 'overview', labelKey: 'DMS.MIGRATION.TAB_OVERVIEW' },
    { id: 'new', labelKey: 'DMS.MIGRATION.TAB_NEW' },
    { id: 'connections', labelKey: 'DMS.MIGRATION.TAB_CONNECTIONS' },
    { id: 'jobs', labelKey: 'DMS.MIGRATION.TAB_JOBS' },
    { id: 'mapping', labelKey: 'DMS.MIGRATION.TAB_MAPPING' },
    { id: 'validation', labelKey: 'DMS.MIGRATION.TAB_VALIDATION' },
    { id: 'history', labelKey: 'DMS.MIGRATION.TAB_HISTORY' },
    { id: 'errors', labelKey: 'DMS.MIGRATION.TAB_ERRORS' },
    { id: 'reconciliation', labelKey: 'DMS.MIGRATION.TAB_RECON' },
    { id: 'settings', labelKey: 'DMS.MIGRATION.TAB_SETTINGS' }
  ];

  ngOnInit(): void {
    this.api.probe();
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const tab = params.get('tab') as MigrationTab | null;
      this.tab.set(this.tabs.some((t) => t.id === tab) ? (tab as MigrationTab) : 'overview');
    });
  }

  select(tab: MigrationTab): void {
    if (tab === 'overview') {
      this.router.navigate(['/settings/data-migration']);
    } else {
      this.router.navigate(['/settings/data-migration', tab]);
    }
  }

  /** Deep-link helper for child components ("go to the jobs tab"). */
  go(tab: MigrationTab): void {
    this.select(tab);
  }
}
